/**
 * Who Naurist invoices: the workspace's own billing profile, read and written.
 *
 * One record per workspace and nothing keyed by an id — the bearer token says
 * whose it is, the same way `subscriptions/current/` works. Not to be confused
 * with `My brand`, which is what a *dietitian's own clients* see on their meal
 * plans: this is the name and address our invoice to the practice is headed
 * with, and a practice may well be billed somewhere other than where it
 * trades. See `BillingDetails`, where that distinction is set out.
 *
 * Both verbs answer inside a `billing_profile` envelope; the `PATCH` takes the
 * record bare. Everything snake_case stops here.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import { ApiError } from "../../../lib/http";
import { toInternationalNumber, toNationalNumber } from "../../../lib/fieldFormat";
import type { GetToken } from "../../auth/types/auth.types";
import type {
  BillingDetails,
  BillingProfileEnvelope,
  BillingProfileResponse,
} from "../types/billing.types";

/**
 * The only country this form can describe.
 *
 * Sent rather than collected: every field around it is India-shaped — a
 * six-digit PIN code, a ten-digit mobile behind a painted `+91`, a GSTIN — so a
 * country picker would be a control with one answer. The day Naurist bills
 * anywhere else this becomes a field on `BillingDetails` and a row on the form,
 * and this constant is where that starts.
 */
export const COUNTRY = "IN";

const token = async (getToken: GetToken, what: string): Promise<string> => {
  const value = await getToken();
  if (!value) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${what}.`);
  }
  return value;
};

/** The saved record as the form holds it. */
export const toBillingDetails = (
  response: BillingProfileResponse
): BillingDetails => ({
  fullName: response.legal_name ?? "",
  email: response.email ?? "",
  // The country code comes off here and goes back on in `toRequest`. Every
  // phone field in the app holds the ten digits alone, with the `+91` painted
  // beside them rather than stored in them.
  phone: toNationalNumber(response.phone_number ?? ""),
  gstin: response.gstin ?? "",
  address: response.address_line ?? "",
  city: response.city ?? "",
  state: response.state ?? "",
  pincode: response.postal_code ?? "",
});

/** The form as the endpoint takes it. */
export const toBillingProfileRequest = (
  details: BillingDetails
): BillingProfileResponse => ({
  legal_name: details.fullName.trim(),
  email: details.email.trim(),
  phone_number: toInternationalNumber(details.phone),
  // Sent even when empty, because empty is an answer: a practice that has
  // removed its GSTIN is telling us it has none, and a field left out of a
  // PATCH would leave the old one standing.
  gstin: details.gstin.trim(),
  address_line: details.address.trim(),
  city: details.city.trim(),
  state: details.state.trim(),
  postal_code: details.pincode.trim(),
  country: COUNTRY,
});

/**
 * Whether there is anything in a profile worth opening the form on.
 *
 * The endpoint answers with a record of empty strings as readily as with
 * nothing at all, and both mean the same thing: this workspace has not told us
 * who to invoice yet. The checkout uses this to decide whether to fall back to
 * `My brand` — see `BillingCheckoutPage`.
 */
export const hasBillingDetails = (details: BillingDetails): boolean =>
  Object.values(details).some((value) => value.trim() !== "");

/**
 * Who this workspace is invoiced, or `null` for one that has never said.
 *
 * A 404 is that `null` rather than a failure, exactly as it is for
 * `subscriptions/current/`: never having filled the form in is the ordinary
 * state of a workspace before its first purchase, and the checkout has a
 * perfectly good screen for it.
 */
export async function fetchBillingProfile(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<BillingDetails | null> {
  try {
    const response = await api.get<BillingProfileEnvelope | null>(
      API_ENDPOINTS.billing.profile,
      { token: await token(getToken, "load your billing details"), signal }
    );

    const profile = response?.billing_profile;
    if (!profile) return null;

    const details = toBillingDetails(profile);
    return hasBillingDetails(details) ? details : null;
  } catch (cause) {
    if (cause instanceof ApiError && cause.status === 404) return null;
    throw cause;
  }
}

/**
 * Records who to invoice, and answers with what was stored.
 *
 * The answer is mapped back rather than assumed: the backend trims, upper-cases
 * a GSTIN and may well normalise an address, and the form should end up holding
 * what the invoice will actually carry rather than what was typed at it.
 */
export async function saveBillingProfile(
  getToken: GetToken,
  details: BillingDetails,
  signal?: AbortSignal
): Promise<BillingDetails> {
  const response = await api.patch<BillingProfileEnvelope>(
    API_ENDPOINTS.billing.profile,
    toBillingProfileRequest(details),
    { token: await token(getToken, "save your billing details"), signal }
  );

  // A 200 with nothing in the envelope is not worth throwing over — the write
  // went through, and what was sent is the best account of it there is.
  return response?.billing_profile
    ? toBillingDetails(response.billing_profile)
    : details;
}
